<template>
    <div class="iframe-container" v-bind:class="{ embed: isEmbedOnMainPage }">
        <div v-if="errorMsg" class="error">
            {{ errorMsg }}
        </div>
        <div
            v-else
            v-bind:id="CONTAINER_ID"
            class="betGeniusStream"
        />
    </div>
</template>

<script lang="ts" setup>
    import { useEventListener, useScriptTag } from '@vueuse/core';
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, onMounted, onUnmounted, ref } from 'vue';
    import { useEventEmitter } from '@/composable/useEventEmitter';
    import { Api } from '@/core/lib/api';
    import { getBetGeniusScriptUrl } from '@/core/lib/url';
    import type { IBetGenius } from '@/interface/IEvent';
    import { LiveStreamResource } from '@/interface/pollux';
    import { useToggleStore } from '@/store/toggleStore';

    const props = defineProps({
        betGenius: {
            type: Object as PropType<IBetGenius>,
            required: true,
        },
        isEmbedOnMainPage: {
            type: Boolean,
            default: false,
        },
    });

    const errorMsg = ref('');
    const isPlayerReady = ref(false);

    const BET_GENIUS_MESSAGE_BUS = 'geniussportsmessagebus';
    const CONTAINER_ID = 'bet-genius-player-container';

    async function onMessage(event: Event) {
        try {
            const customEvent = event as CustomEvent;
            if (customEvent.detail?.type === 'player_ready') {
                isPlayerReady.value = true;
                const { data: { url, errorCode, errorMessage } } = await Api.getLiveStream(props.betGenius.eventId, 0, LiveStreamResource.BetGenius);

                if (errorCode === 0 && url) {
                    const parsed = JSON.parse(url);
                    window.GeniusLivePlayer.player.start(parsed);
                } else if (errorMessage) {
                    errorMsg.value = errorMessage;
                }
            } else if (customEvent.detail?.type === 'player_not_ready') {
                const errorsArray = customEvent.detail.body.error;
                errorMsg.value = `Error code: ${errorsArray[0]?.code}. ${errorsArray[0]?.message}.`;
            }
        } catch (error) {
            errorMsg.value = `Failed to load live stream. ${error}`;
        }
    }

    const { betGeniusCustomerId } = storeToRefs(useToggleStore());
    const src = computed(() => getBetGeniusScriptUrl(
        betGeniusCustomerId.value,
        props.betGenius.providerEventId.toString(),
        CONTAINER_ID,
    ));

    useEventListener(window, BET_GENIUS_MESSAGE_BUS, onMessage);

    const { load, unload } = useScriptTag(
        src,
        () => { },
        { manual: true },
    );

    onMounted(async () => {
        await load();
    });

    const eventEmitter = useEventEmitter();
    eventEmitter.on('liveStream:matchClosed', (matchId: number) => {
        eventEmitter.emit('liveStream:streamClosed', matchId);
    });

    onUnmounted(() => {
        if (isPlayerReady.value && window.GeniusLivePlayer?.player) {
            try {
                window.GeniusLivePlayer.player.close();
            } catch {
                // Third-party SDK can throw internally (e.g. closeGSPlayer is not a function)
            }
        }
        unload();
    });
</script>

<style lang="scss" scoped>
    @import "@/components/liveStream/betGeniusStream/BetGeniusStream";

    .betGeniusStream {
        width: 100%;
        height: 100%;
    }
</style>
